import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Image,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from "@mantine/dropzone";
import { IconAlertCircle, IconUpload } from "@tabler/icons";
import { DATA_CURRENCY } from "../../data/options";
import { useServerApi } from "../../hooks/userServerApi";
import { getFileUrl } from "../fileList";
import McaTimeline from "../mca/McaTimeline";

export const expenseTypeId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || "");
  return String(value);
};

const THRESHOLD_OPTIONS = [
  { value: "NONE", label: "Regardless of amount" },
  { value: "AMOUNT", label: "HKD amount" },
  { value: "PER_ATTENDEE", label: "HKD amount per attendee" },
  { value: "BLOCKED_ABOVE", label: "Approvers up to the amount, blocked above it" },
];

const roleText = (value) => (Array.isArray(value) ? value.join(", ") : value || "");

const parseRoles = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

export const ExpenseRoleListField = ({ form, name, label, description }) => (
  <TextInput
    label={label}
    description={description}
    value={roleText(form?.values?.[name])}
    onChange={(event) => form.setFieldValue(name, parseRoles(event.currentTarget.value))}
  />
);

export const ExpenseThresholdModeField = ({ value, onChange }) => (
  <Select
    label="Threshold"
    data={THRESHOLD_OPTIONS}
    value={value || "NONE"}
    onChange={(next) => onChange(next || "NONE")}
  />
);

export const ExpenseItemsField = ({ form, value, disabled, readOnly }) => {
  const locked = Boolean(disabled || readOnly);
  const items = Array.isArray(value) ? value : form?.values?.items || [];
  const setItems = (next) => {
    if (locked) return;
    form.setFieldValue("items", next);
  };

  return (
    <Stack spacing="xs">
      <Text size="sm" weight={500}>
        Receipt lines
      </Text>
      {items.map((item, index) => (
        <Group key={`${index}-${item.name}`} grow align="flex-end">
          <TextInput
            label={index === 0 ? "Item" : undefined}
            value={item.name || ""}
            disabled={locked}
            onChange={(event) => {
              const next = [...items];
              next[index] = { ...item, name: event.currentTarget.value };
              setItems(next);
            }}
          />
          <NumberInput
            label={index === 0 ? "Qty" : undefined}
            value={item.quantity ?? ""}
            hideControls
            disabled={locked}
            onChange={(nextValue) => {
              const next = [...items];
              next[index] = { ...item, quantity: nextValue === "" ? null : nextValue };
              setItems(next);
            }}
          />
          <NumberInput
            label={index === 0 ? "Amount" : undefined}
            value={item.amount ?? ""}
            precision={2}
            hideControls
            disabled={locked}
            onChange={(nextValue) => {
              const next = [...items];
              next[index] = { ...item, amount: nextValue === "" ? null : nextValue };
              setItems(next);
            }}
          />
          <Button
            variant="subtle"
            color="red"
            disabled={locked}
            onClick={() => setItems(items.filter((_, itemIndex) => itemIndex !== index))}
          >
            Remove
          </Button>
        </Group>
      ))}
      <Group>
        <Button
          disabled={locked}
          onClick={() => setItems([...items, { name: "", quantity: null, amount: null }])}
        >
          Add line
        </Button>
      </Group>
    </Stack>
  );
};

const rememberExpenseType = (form, type) => {
  form?.setFieldValue("expenseTypeMode", type?.thresholdMode || "");
};

export const DepartmentSelect = ({
  form,
  value,
  onChange,
  label = "Department",
  description = "The same department record as the user.",
  disabled,
  syncName = false,
}) => {
  const [api] = useServerApi();
  const [options, setOptions] = useState([]);
  const selectedId = expenseTypeId(value);

  useEffect(() => {
    let active = true;
    api
      .search({
        apiEntity: "department",
        pageSize: 100,
        preQuery: { active: true },
        sort: { by: "name", order: "asc" },
      })
      .then((data) => {
        if (!active) return;
        const docs = data?.docs || data || [];
        setOptions(Array.isArray(docs) ? docs : []);
      })
      .catch(() => {
        if (active) setOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <Select
      key={options.length ? "departments-ready" : "departments-loading"}
      label={label}
      description={description || undefined}
      searchable
      clearable
      disabled={disabled}
      data={options.map((department) => ({ value: String(department._id), label: department.name }))}
      value={selectedId || null}
      onChange={(next) => {
        onChange(next || null);
        if (!syncName || !form) return;
        const selected = options.find((department) => String(department._id) === String(next));
        form.setFieldValue("department", selected?.name || "");
      }}
    />
  );
};

export const ExpenseTypeSelect = ({ form, value, onChange, label = "Expense type", disabled, readOnly }) => {
  const [api] = useServerApi();
  const [options, setOptions] = useState([]);
  const selectedId = expenseTypeId(value);

  useEffect(() => {
    let active = true;
    api
      .search({
        apiEntity: "expenseType",
        pageSize: 100,
        preQuery: { active: true },
        sort: { by: "name", order: "asc" },
      })
      .then((data) => {
        if (!active) return;
        const docs = data?.docs || data || [];
        setOptions(Array.isArray(docs) ? docs : []);
      })
      .catch(() => {
        if (active) setOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const selected = options.find((type) => String(type._id) === selectedId);
    if (!selected || !form) return;
    if (value && typeof value === "object") onChange(selectedId);
    if ((form.values?.expenseTypeMode || "") !== (selected.thresholdMode || "")) {
      form.setFieldValue("expenseTypeMode", selected.thresholdMode || "");
    }
  }, [options, selectedId]);

  return (
    <Select
      label={label}
      searchable
      disabled={Boolean(disabled || readOnly)}
      data={options.map((type) => ({ value: String(type._id), label: type.name }))}
      value={selectedId || null}
      onChange={(next) => {
        onChange(next || "");
        rememberExpenseType(
          form,
          options.find((type) => String(type._id) === String(next))
        );
      }}
    />
  );
};

const currencyCode = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

const isHkdCurrency = (value) => {
  const code = currencyCode(value);
  return !code || code === "$" || code === "HKD" || code === "HK$" || code === "HK";
};

export const lookupInternalRate = (currency, rates) => {
  const code = currencyCode(currency);
  if (isHkdCurrency(code)) return 1;
  const wanted = code === "RMB" || code === "CNY" ? ["RMB", "CNY"] : [code];
  const row = (rates || []).find((rate) => wanted.includes(currencyCode(rate?.code)));
  const value = Number(row?.value);
  return Number.isFinite(value) ? value : null;
};

export const amountInHkd = (total, currency, rates) => {
  if (total == null || total === "") return null;
  const numeric = Number(total);
  if (!Number.isFinite(numeric)) return null;
  const rate = lookupInternalRate(currency, rates);
  if (rate == null) return null;
  return Math.round(numeric * rate * 100) / 100;
};

const useInternalRates = () => {
  const [api] = useServerApi();
  const [rates, setRates] = useState(null);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams({ currency: "HKD", total: "0" });
    api.ExpenseClaim
      .approvalPreview(params)
      .then((result) => {
        if (!active) return;
        setRates(result?.data?.internalRates || []);
      })
      .catch(() => {
        if (active) setRates([]);
      });
    return () => {
      active = false;
    };
    // Load the active rate table once. The API object is a new identity each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return rates;
};

export const ExpenseCurrencySelect = ({ value, onChange, disabled }) => (
  <Select
    label="Currency"
    searchable
    disabled={disabled}
    data={DATA_CURRENCY}
    value={isHkdCurrency(value) ? "HKD" : value || null}
    onChange={(next) => onChange(next || "")}
  />
);

export const ExpenseAmountHkdField = ({ form, value, onChange, disabled }) => {
  const rates = useInternalRates();
  const currency = form?.values?.currency || "";
  const total = form?.values?.total;
  const hkd = isHkdCurrency(currency);
  const rate = rates == null ? null : lookupInternalRate(currency || "HKD", rates);
  const signature = `${currency}|${total}|${rates == null ? "loading" : "ready"}`;
  const seen = useRef(null);

  useEffect(() => {
    if (rates == null) return undefined;
    const previous = seen.current;
    seen.current = signature;
    const first = previous == null;
    if (first && value != null && value !== "") return undefined;
    const next = amountInHkd(total, currency || "HKD", rates);
    if (next == null) {
      if (!first && value != null && value !== "") onChange(null);
      return undefined;
    }
    if (Number(value) !== next) onChange(next);
    return undefined;
    // The setter identity changes every render. The signature stops the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const description = hkd
    ? "Same as the receipt total. Approval uses this amount."
    : rate != null
      ? `${currency} × ${rate} internal rate. Approval uses this amount, not the foreign total.`
      : rates != null
        ? `No internal rate is set for ${currency}. Enter the HKD amount. Approval uses this amount.`
        : "Approval uses this amount, not the foreign total.";

  return (
    <NumberInput
      label="Amount in HKD"
      description={description}
      precision={2}
      hideControls
      disabled={disabled || hkd}
      value={value == null || value === "" ? undefined : Number(value)}
      onChange={onChange}
    />
  );
};

export const applyCapturedReceipt = (form, patch, file) => {
  if (!form) return;
  const currency = patch.currency || "HKD";
  const total = patch.total != null ? patch.total : form.values?.total;
  form.setValues({
    ...form.values,
    receiptFile: file || form.values?.receiptFile || null,
    merchant: patch.merchant || form.values?.merchant || "",
    currency,
    receiptDate: patch.receiptDate
      ? new Date(patch.receiptDate)
      : form.values?.receiptDate,
    subtotal: patch.subtotal != null ? patch.subtotal : form.values?.subtotal,
    tax: patch.tax != null ? patch.tax : form.values?.tax,
    total,
    amountHkd: currency === "HKD" && total != null ? total : null,
    expenseType: patch.expenseTypeId
      ? String(patch.expenseTypeId)
      : form.values?.expenseType,
    items: Array.isArray(patch.items) ? patch.items : form.values?.items,
  });
};

const receiptKind = (file) => {
  const type = `${file?.mimetype || ""} ${file?.name || file?.filename || ""}`.toLowerCase();
  if (type.includes("pdf")) return "pdf";
  if (/png|jpe?g|gif|webp|image/.test(type)) return "image";
  return "file";
};

export const ReceiptPreview = ({ file, form }) => {
  const source = file || form?.values?.receiptFile;
  const url = getFileUrl(source);
  const name = source?.name || source?.filename || "Receipt";
  if (!url) {
    return (
      <Text size="sm" color="dimmed">
        The receipt file will show here.
      </Text>
    );
  }
  const kind = receiptKind(source);
  return (
    <Stack spacing="xs">
      {kind === "pdf" ? (
        <iframe
          title={name}
          src={url}
          style={{
            width: "100%",
            height: 460,
            border: "1px solid #dee2e6",
            background: "#fff",
          }}
        />
      ) : kind === "image" ? (
        <Image
          src={url}
          alt={name}
          fit="contain"
          height={460}
          styles={{ image: { background: "#f8f9fa" } }}
        />
      ) : (
        <Text size="sm">{name}</Text>
      )}
      <Anchor href={url} target="_blank" size="xs">
        Open the file
      </Anchor>
    </Stack>
  );
};

export const ExpenseReceiptDropzone = ({ form, onBusyChange, disabled, readOnly }) => {
  const [api] = useServerApi();
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const openRef = useRef(null);
  const file = form?.values?.receiptFile;
  const locked = Boolean(disabled || readOnly);

  const handleDrop = async (files) => {
    if (locked) return;
    const dropped = files?.[0];
    if (!dropped) return;
    setUploading(true);
    onBusyChange?.(true);
    setStatus({ color: "blue", message: "Reading the receipt..." });
    try {
      const uploaded = await api.uploadFile(
        "expense",
        dropped,
        form.values?._id || "draft",
        file ? [file] : [],
        { entityType: "expenseClaim", entityField: "receiptFile" }
      );
      const stored = uploaded?.data;
      if (!stored) throw new Error("Could not store the receipt");
      const captured = await api.ExpenseClaim.captureReceipt(stored);
      const patch = captured?.data || {};
      applyCapturedReceipt(form, patch, stored);
      const filled = patch.merchant || patch.total != null;
      setStatus({
        color: filled ? "green" : "yellow",
        message: filled
          ? "Receipt read. Check the amounts below before you save."
          : "The receipt was uploaded, but no amounts were found. Fill them in by hand.",
      });
    } catch (error) {
      setStatus({
        color: "red",
        message: error?.message || "Could not read this receipt. Fill the amounts in by hand.",
      });
    } finally {
      setUploading(false);
      onBusyChange?.(false);
    }
  };

  return (
    <Stack spacing="xs">
      <Group position="apart">
        <Text size="sm">Upload the receipt here. It is read and the amounts are filled in.</Text>
        <Button
          size="xs"
          variant="light"
          leftIcon={<IconUpload size={14} />}
          loading={uploading}
          disabled={locked}
          onClick={() => {
            if (!locked) openRef.current?.();
          }}
        >
          {file?.name ? "Upload again" : "Upload receipt"}
        </Button>
      </Group>
      <Dropzone
        openRef={openRef}
        onDrop={handleDrop}
        disabled={locked}
        styles={
          locked
            ? { root: { opacity: 0.55, pointerEvents: "none" } }
            : undefined
        }
        onReject={() =>
          setStatus({
            color: "red",
            message: "Use a JPG, PNG, or PDF receipt.",
          })
        }
        accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
        multiple={false}
        loading={uploading}
      >
        <Group position="center" spacing="xs" mih={48}>
          <IconUpload size={18} />
          <Text size="sm">
            {uploading ? "Reading the receipt..." : "Or drop a receipt image or PDF here"}
          </Text>
        </Group>
      </Dropzone>
      {file?.name ? (
        <Text size="xs" color="dimmed">
          {file.name}
        </Text>
      ) : null}
      {status ? (
        <Alert color={status.color} icon={<IconAlertCircle size={16} />}>
          {status.message}
        </Alert>
      ) : null}
    </Stack>
  );
};

export const ExpenseClaimSummary = ({ form, onPreview }) => {
  const [api] = useServerApi();
  const [preview, setPreview] = useState(null);
  const values = form?.values || {};
  const previewKey = [
    values.expenseType,
    values.currency,
    values.total,
    values.amountHkd,
    values.attendeeCount,
    Array.isArray(values.preApprovalFiles) ? values.preApprovalFiles.length : 0,
  ].join("|");

  useEffect(() => {
    if (!values.expenseType) {
      setPreview(null);
      return undefined;
    }
    let active = true;
    const typeId = expenseTypeId(values.expenseType);
    if (!typeId) {
      setPreview(null);
      return undefined;
    }
    const params = new URLSearchParams({
      expenseType: typeId,
      currency: values.currency || "",
      total: values.total == null ? "" : String(values.total),
      amountHkd: values.amountHkd == null ? "" : String(values.amountHkd),
      attendeeCount: values.attendeeCount == null ? "" : String(values.attendeeCount),
      preApprovalAttached: values.preApprovalFiles?.length ? "true" : "false",
    });
    api.ExpenseClaim.approvalPreview(params)
      .then((result) => {
        if (active) setPreview(result?.data || null);
      })
      .catch(() => {
        if (active) setPreview(null);
      });
    return () => {
      active = false;
    };
    // previewKey already includes the fields that should refresh the route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  useEffect(() => {
    onPreview?.(preview);
  }, [preview, onPreview]);

  const reason = preview?.submitBlockReason || values.submitBlockReason;
  const route = preview?.approvalRouteLabel || values.approvalRouteLabel;
  const perAttendee = preview?.amountPerAttendee ?? values.amountPerAttendee;
  const amountHkd = preview?.amountHkd ?? values.amountHkd;

  return (
    <Stack spacing="xs">
      {amountHkd != null && amountHkd !== "" ? (
        <Text size="sm">Amount in HKD: {Number(amountHkd).toFixed(2)}</Text>
      ) : null}
      {route ? <Text size="sm">Approval route: {route}</Text> : null}
      {perAttendee != null ? (
        <Text size="sm">Amount per attendee: HK${Number(perAttendee).toFixed(2)}</Text>
      ) : null}
      {reason ? (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
          {reason}
        </Alert>
      ) : null}
    </Stack>
  );
};

const linkedRequestId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id ? String(value._id) : "";
  return String(value);
};

export const approvalStatusLabel = (claimStatus, requestStatus) => {
  if (requestStatus === "REJECTED") return { label: "Rejected", color: "red" };
  if (requestStatus === "APPROVED" || claimStatus === "APPROVED") {
    return { label: "Approved", color: "green" };
  }
  if (requestStatus === "PENDING" || requestStatus === "IN_PROGRESS") {
    return { label: "In approval", color: "teal" };
  }
  if (requestStatus === "CANCELLED") return { label: "Cancelled", color: "gray" };
  if (requestStatus === "TERMINATED") return { label: "Terminated", color: "gray" };
  if (requestStatus === "EXPIRED") return { label: "Expired", color: "gray" };
  return { label: "Draft", color: "orange" };
};

export const ExpenseClaimStatus = ({ form }) => {
  const [api] = useServerApi();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const values = form?.values || {};
  const claimId = values._id ? String(values._id) : "";
  const storedRequestId = linkedRequestId(values.mcaRequestId);

  useEffect(() => {
    if (!claimId && !storedRequestId) {
      setRequest(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    const load = async () => {
      let requestId = storedRequestId;
      if (!requestId && claimId) {
        const found = await api.search({
          apiEntity: "mcaRequest",
          pageSize: 1,
          byAggregation: false,
          searchQuery: { targetEntity: "expenseClaim", targetEntityId: claimId },
          sort: { by: "createdAt", order: "desc" },
        });
        const docs = found?.docs || [];
        const match = docs.find(
          (doc) =>
            doc?.targetEntity === "expenseClaim" &&
            String(doc?.targetEntityId) === claimId
        );
        requestId = match?._id ? String(match._id) : "";
      }
      if (!requestId) return null;
      return api.getById({ apiEntity: "mcaRequest", id: requestId });
    };
    load()
      .then((data) => {
        if (active) setRequest(data || null);
      })
      .catch(() => {
        if (active) setRequest(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // The API object is a new identity each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, storedRequestId]);

  const linkedStatus =
    values.mcaRequestId && typeof values.mcaRequestId === "object"
      ? values.mcaRequestId.status
      : "";
  const badge = approvalStatusLabel(values.status, request?.status || linkedStatus);

  return (
    <Stack spacing="sm">
      <Badge
        color={badge.color}
        variant="light"
        size="lg"
        sx={{ alignSelf: "flex-start" }}
      >
        {badge.label}
      </Badge>
      {request?.stepResults?.length ? (
        <McaTimeline request={request} plain showSubmission />
      ) : loading ? (
        <Text size="sm" color="dimmed">
          Loading the approval progress.
        </Text>
      ) : badge.label === "Draft" ? (
        <Text size="sm" color="dimmed">
          Not submitted
        </Text>
      ) : null}
    </Stack>
  );
};
